import { useEffect, useState } from 'react'
import { Button, Modal, Select, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { STAGE_LABEL, nextStage } from '../labels'
import { useLawyers, useRouteDocument } from '../hooks'
import type { DocumentRow, RoutingStage } from '../types'

/** Modal „Przekaż dalej" — wybór etapu i (dla „Przekazane prawnikowi") prawnika. */
export function RouteModal({
  document,
  onClose,
}: {
  document: DocumentRow | null
  onClose: () => void
}) {
  const toast = useToast()
  const route = useRouteDocument()
  const target = nextStage(document?.routing_stage ?? null)
  const lawyers = useLawyers(Boolean(document))
  const [userId, setUserId] = useState('')

  useEffect(() => {
    setUserId(document?.routed_to?.id ? String(document.routed_to.id) : '')
  }, [document])

  const submit = async () => {
    if (!document || !target) return
    try {
      await route.mutateAsync({
        id: document.id,
        stage: target,
        to_user_id: userId ? Number(userId) : null,
      })
      toast.success(`Przekazano: ${STAGE_LABEL[target]}`)
      onClose()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.userMessage : 'Nie udało się przekazać.')
    }
  }

  return (
    <Modal
      open={Boolean(document)}
      onClose={onClose}
      title="Przekaż dalej"
      subtitle={document ? document.title : undefined}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={route.isPending}
            disabled={!target || (target === 'assigned' && !userId)}
          >
            Przekaż
          </Button>
        </>
      }
    >
      {target ? (
        <>
          <p className="text-base text-muted">
            Etap docelowy: <span className="font-semibold text-ink">{STAGE_LABEL[target]}</span>
          </p>
          <div className="mt-4">
            <Select
              label={target === 'assigned' ? 'Prawnik prowadzący' : 'Partner akceptujący'}
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              placeholder={target === 'assigned' ? 'wybierz prawnika' : undefined}
              options={[
                ...(target === 'assigned' ? [] : [{ value: '', label: 'bez wskazania' }]),
                ...(lawyers.data ?? []).map((lawyer) => ({
                  value: String(lawyer.id),
                  label: lawyer.title ? `${lawyer.full_name} — ${lawyer.title}` : lawyer.full_name,
                })),
              ]}
            />
          </div>
        </>
      ) : (
        <p className="text-base text-muted">Dokument jest na ostatnim etapie obiegu.</p>
      )}
    </Modal>
  )
}

export type { RoutingStage }
